import type { Order } from 'pkg/api/models/order';
import {
	ChargeSourceType,
	type OrderChange,
} from 'pkg/api/models/order_charge';
import type { OrderRefund, RefundPayload } from 'pkg/api/models/order_refund';
import {
	OrderRefundStatus,
	OrderRefundType,
} from 'pkg/api/models/order_refund';

// chargeRefundInProgress reports whether a refund still waiting on the provider holds up the payment, as
// the backend counts it: a refund of the payment itself, or a refund not linked to any payment. An
// unlinked card refund or chargeback returns card money, so it holds up only card payments; any other
// unlinked refund holds up every payment.
export function chargeRefundInProgress(
	refunds: OrderRefund[] | undefined,
	charge: OrderChange
): boolean {
	return (refunds ?? []).some((r) => {
		if (
			r.status === OrderRefundStatus.OrderRefundStatusAccepted ||
			r.status === OrderRefundStatus.OrderRefundStatusRejected
		) {
			return false;
		}

		if (r.orderChargeId) {
			return r.orderChargeId === charge.id;
		}

		const returnsCardMoney =
			r.type === OrderRefundType.OrderRefundTypeRefund ||
			r.type === OrderRefundType.OrderRefundTypeChargeback;

		return charge.sourceType === ChargeSourceType.Provider || !returnsCardMoney;
	});
}

// chargeRefundWaiting is how much of the payment's own refunds is still waiting on the provider.
export function chargeRefundWaiting(
	refunds: OrderRefund[] | undefined,
	chargeId: number
): number {
	return (refunds ?? [])
		.filter(
			(r) =>
				r.orderChargeId === chargeId &&
				r.status !== OrderRefundStatus.OrderRefundStatusAccepted &&
				r.status !== OrderRefundStatus.OrderRefundStatusRejected
		)
		.reduce((sum, r) => sum + r.amount, 0);
}

// amountGoingOut is what a refund request sends out. Started is what the refund endpoint answers with
// when it refunded each card payment of an order paid more than once by card.
export function amountGoingOut(
	order: Order,
	request: RefundPayload,
	started: OrderRefund[] = []
): number {
	if (request.amount) {
		return request.amount;
	}
	if (request.charges?.length) {
		return request.charges.reduce(
			(sum, c) =>
				sum +
				(c.amount ??
					order.charges?.find((charge) => charge.id === c.chargeId)
						?.refundableLeft ??
					0),
			0
		);
	}
	if (started.length) {
		return started.reduce((sum, r) => sum + r.amount, 0);
	}
	return order.remainingRefundable ?? order.amountDue - order.amountRefunded;
}
